import styled from "styled-components";

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

export const Section = styled.div`
  background: var(--surface);
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  border: 1px solid var(--accent-light);
`;

export const SectionSubTitle = styled.h3`
  font-size: 18px;
  color: var(--text-primary);
  margin-bottom: 20px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
`;

export const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const Label = styled.label`
  font-weight: 500;
  color: var(--text-secondary);
  font-size: 14px;
`;

export const Input = styled.input`
  padding: 12px;
  border: 2px solid var(--accent-light);
  border-radius: 8px;
  font-size: 15px;
  transition: border-color 0.3s;
  background: var(--surface);
  color: var(--text-primary);

  &:focus {
    outline: none;
    border-color: var(--accent);
  }

  &::placeholder {
    color: var(--text-muted);
  }
`;

export const FileUploadWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  flex-wrap: wrap;
`;

export const FileInput = styled.input`
  display: none;
`;

export const FileLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 12px 24px;
  background: linear-gradient(
    135deg,
    var(--accent) 0%,
    var(--accent-dark) 100%
  );
  color: white;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  transition:
    transform 0.2s,
    box-shadow 0.2s;
  border: none;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(var(--accent-rgb), 0.3);
  }
`;

export const EmailCount = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #28a745;
  font-weight: 500;
  padding: 8px 16px;
  background: rgba(40, 167, 69, 0.1);
  border-radius: 6px;
`;

export const AttachmentPreview = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  background: var(--secondary);
  border: 1px solid var(--accent-light);
  border-radius: 8px;
  margin-top: 15px;
  max-width: 100%;
`;

export const AttachmentThumb = styled.img`
  width: 56px;
  height: 56px;
  object-fit: cover;
  border-radius: 6px;
  flex-shrink: 0;
`;

export const AttachmentInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;

  .name {
    font-size: 14px;
    font-weight: 500;
    color: var(--text-primary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .size {
    font-size: 12px;
    color: var(--text-muted);
  }
`;

export const RemoveAttachmentButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: #dc3545;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.2s;

  &:hover {
    background: rgba(220, 53, 69, 0.1);
  }
`;

export const TestEmailWrapper = styled.div`
  display: flex;
  gap: 15px;
  align-items: flex-end;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 12px 24px;
  background: ${(props) =>
    props.$primary
      ? "linear-gradient(135deg, var(--accent) 0%, var(--accent-dark) 100%)"
      : "var(--secondary)"};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  white-space: nowrap;
  width: ${(props) => (props.$fullWidth ? "100%" : "auto")};

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(var(--accent-rgb), 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

export const ProgressWrapper = styled.div`
  margin-top: 20px;
`;

export const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: var(--accent-light);
  border-radius: 4px;
  overflow: hidden;
`;

export const ProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, var(--accent) 0%, var(--accent-dark) 100%);
  transition: width 0.8s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;

  /* Success state - when progress reaches 100% */
  ${(props) =>
    props.$isComplete &&
    `
    animation: successPulse 2s ease-in-out infinite;
    
    @keyframes successPulse {
      0%, 100% {
        background: linear-gradient(90deg, #28a745 0%, #20c997 100%);
      }
      50% {
        background: linear-gradient(90deg, #20c997 0%, #28a745 100%);
      }
    }
  `}

  /* Loading shimmer effect only while loading */
  ${(props) =>
    !props.$isComplete &&
    `
    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(
        90deg,
        transparent 0%,
        rgba(255, 255, 255, 0.3) 50%,
        transparent 100%
      );
      animation: shimmer 1.5s infinite;
      transform: translateX(-100%);
    }

    @keyframes shimmer {
      0% {
        transform: translateX(-100%);
      }
      50% {
        transform: translateX(0%);
      }
      100% {
        transform: translateX(100%);
      }
    }
  `}
`;

export const ProgressText = styled.p`
  text-align: center;
  margin-top: 10px;
  color: var(--accent);
  font-weight: 600;
`;

export const Results = styled.div`
  margin-top: 25px;
  padding: 30px;

  border-radius: 16px;
  position: relative;
  overflow: hidden;

  @keyframes shimmer {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.7;
    }
  }
`;

export const ResultsTitle = styled.h3`
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: center;
  margin-bottom: 25px;
  color: var(--text-primary);
  font-size: 20px;
  font-weight: 600;

  svg {
    animation: ${(props) => (props.$success ? "successPulse" : "warningPulse")}
      2s ease-in-out infinite;
  }

  @keyframes successPulse {
    0%,
    100% {
      transform: scale(1);
      color: #28a745;
    }
    50% {
      transform: scale(1.1);
      color: #20c997;
    }
  }

  @keyframes warningPulse {
    0%,
    100% {
      transform: scale(1);
      color: #ffc107;
    }
    50% {
      transform: scale(1.05);
      color: #fd7e14;
    }
  }
`;

export const ResultsStats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 20px;
  margin-bottom: 25px;
`;

export const Stat = styled.div`
  text-align: center;
  padding: 20px;
  background: ${(props) =>
    props.$success
      ? "linear-gradient(135deg, rgba(40, 167, 69, 0.05) 0%, rgba(40, 167, 69, 0.1) 100%)"
      : props.$error
        ? "linear-gradient(135deg, rgba(220, 53, 69, 0.05) 0%, rgba(220, 53, 69, 0.1) 100%)"
        : "linear-gradient(135deg, rgba(var(--accent-rgb), 0.05) 0%, rgba(var(--accent-rgb), 0.1) 100%)"};
  border-radius: 12px;
  border: 2px solid
    ${(props) =>
      props.$success ? "#28a745" : props.$error ? "#dc3545" : "var(--accent)"};
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${(props) =>
      props.$success
        ? "linear-gradient(90deg, #28a745 0%, #20c997 100%)"
        : props.$error
          ? "linear-gradient(90deg, #dc3545 0%, #fd1d53 100%)"
          : "linear-gradient(90deg, var(--accent) 0%, var(--accent-dark) 100%)"};
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px
      ${(props) =>
        props.$success
          ? "rgba(40, 167, 69, 0.2)"
          : props.$error
            ? "rgba(220, 53, 69, 0.2)"
            : "rgba(124, 14, 51, 0.25)"};
  }
`;

export const StatLabel = styled.div`
  font-size: 13px;
  color: ${(props) =>
    props.$success ? "#28a745" : props.$error ? "#dc3545" : "var(--accent)"};
  margin-bottom: 5px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
`;

export const StatValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: ${(props) =>
    props.$success ? "#28a745" : props.$error ? "#dc3545" : "var(--accent)"};
  margin-top: 8px;
  animation: countUp 0.8s ease-out;

  @keyframes countUp {
    0% {
      opacity: 0;
      transform: translateY(20px) scale(0.8);
    }
    100% {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
`;

export const ErrorList = styled.div`
  background: var(--surface);
  padding: 15px;
  border-radius: 8px;
  margin-top: 15px;

  h4 {
    margin: 0 0 10px 0;
    color: #dc3545;
  }
`;

export const ErrorItem = styled.div`
  padding: 8px;
  background: rgba(220, 53, 69, 0.1);
  margin-bottom: 5px;
  border-radius: 4px;
  font-size: 13px;
  color: #721c24;
  border-left: 3px solid #dc3545;
`;

export const EmailPreview = styled.div`
  background: var(--secondary);
  border-radius: 12px;
  padding: 25px;
  margin-top: 25px;

  h3 {
    margin-bottom: 15px;
    color: var(--text-primary);
    font-size: 18px;
  }
`;

export const EmailList = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  max-height: 400px;
  overflow-y: auto;
  position: relative;
  padding-right: 8px;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: var(--surface);
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background: linear-gradient(
      135deg,
      var(--accent) 0%,
      var(--accent-dark) 100%
    );
    border-radius: 10px;
    transition: background 0.3s;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(
      135deg,
      var(--accent-dark) 0%,
      var(--accent) 100%
    );
  }

  /* Gradient fade at bottom */
  &::after {
    content: "";
    position: sticky;
    bottom: 0;
    left: 0;
    right: 0;
    height: 30px;
    background: linear-gradient(to top, var(--secondary) 0%, transparent 100%);
    pointer-events: none;
    z-index: 1;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const EmailItem = styled.div`
  padding: 12px 16px;
  background: var(--surface);
  border-radius: 8px;
  font-size: 14px;
  color: var(--text-secondary);
  border-left: 4px solid var(--accent);
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    border-left-color: var(--accent-dark);
  }
`;

export const TextArea = styled.textarea`
  padding: 12px;
  border: 2px solid var(--accent-light);
  border-radius: 8px;
  font-size: 15px;
  transition: border-color 0.3s;
  min-height: 150px;
  resize: vertical;
  font-family: inherit;
  background: var(--surface);
  color: var(--text-primary);

  &:focus {
    outline: none;
    border-color: var(--accent);
  }

  &::placeholder {
    color: var(--text-muted);
  }
`;

export const PreviewCard = styled.div`
  background: var(--surface);
  border: 1px solid var(--accent-light);
  border-radius: 12px;
  padding: 20px;
  margin-top: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

export const PreviewHeader = styled.div`
  border-bottom: 1px solid var(--accent-light);
  padding-bottom: 15px;
  margin-bottom: 20px;

  h4 {
    margin: 0 0 10px 0;
    color: var(--text-primary);
    font-size: 16px;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .subject {
    color: var(--text-secondary);
    font-size: 14px;
    font-weight: 500;
  }
`;

export const PreviewContent = styled.div`
  white-space: pre-wrap;
  line-height: 1.6;
  color: var(--text-primary);
  font-size: 14px;

  .placeholder {
    background: rgba(var(--accent-rgb), 0.1);
    color: var(--accent);
    padding: 2px 6px;
    border-radius: 4px;
    font-weight: 500;
  }
`;
